import { AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, QueryList, SimpleChanges, ViewChildren } from '@angular/core';

import { Document, Whitelabel, FooterTypeEnum, FooterEnum } from '@app/models';
import { EnterpriseService, WhitelabelService } from '@app/services';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-footer-preview',
  templateUrl: './footer-preview.component.html',
  styleUrls: ['./footer-preview.component.scss'],
  standalone: false
})
export class FooterPreviewComponent implements AfterViewInit, OnChanges, OnDestroy {
  readonly FooterTypeEnum = FooterTypeEnum;
  @ViewChildren('footerHolder') footerHolders: QueryList<ElementRef<HTMLElement>>;
  @Input() pdfContainer: HTMLElement;
  @Input() pagesCount: number;
  @Input() loadedPages: number;
  @Input() documentFooter: FooterEnum;
  @Input() documentFooterType: FooterTypeEnum;
  @Input() qualified: boolean;
  @Input() documentWhitelabel: Whitelabel;
  @Input() document: Document;
  @Input() isDocumentWhitelabelLoaded: boolean;

  translateQualified: string;
  translatedHash: string;
  translatedQRCode: string;
  translatedValidator: string;
  translatedMiniFooterPage: string[] = [];

  readonly FooterEnum = FooterEnum;
  readonly maxMiniFooterHeight = 23;
  readonly maxMiniFooterSpacing = 14;
  readonly maxFooterHeight = 54;
  private readonly maxPageWidth = 794;
  private readonly maxFooterMargin = 14;
  private resizeObserver?: ResizeObserver;
  private repositionTimeout?: ReturnType<typeof setTimeout>;

  constructor(
    public whitelabelService: WhitelabelService,
    private translateService: TranslateService,
    private enterpriseService: EnterpriseService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.pdfContainer && this.resizeObserver) {
      this.observePdfContainer();
    }

    const lang = this.document?.locale?.language;
    const translations = this.getTranslationsForLang(lang);

    if (!translations) {
      return;
    }

    if (this.documentFooter && (changes.pagesCount || changes.loadedPages || changes.documentFooter || changes.documentFooterType)) {
      setTimeout(() => {
        const parser = this.translateService.parser;

        const hashKey = this.document?.hashes?.sha2 ? 'shared.sha256HashOfOriginalPDF' : 'shared.sha256HashOfOriginalPDFExample';
        const hashTemplate = hashKey.split('.').reduce((obj, part) => obj?.[part], translations);
        this.translatedHash = parser.interpolate(hashTemplate, { sha2: this.document?.hashes?.sha2 });

        const signKey = this.qualified ? 'shared.signedWithQualifiedElectronicSignature' : 'shared.signedWithAdvancedElectronicSignature';
        const signTemplate = signKey.split('.').reduce((obj, part) => obj?.[part], translations);
        this.translateQualified = parser.interpolate(signTemplate, {});

        const isAbleForExclusiveDomains = this.enterpriseService.ableForExclusiveDomains();
        const baseURL = isAbleForExclusiveDomains ? location.hostname + '/verificar' : 'valida.ae';

        const validationKey = this.document?.id ? 'shared.validationLink' : 'shared.validationLinkExample';
        const validationTemplate = validationKey.split('.').reduce((obj, part) => obj?.[part], translations);
        this.translatedQRCode = parser.interpolate(validationTemplate, {
          url: this.qrCodeUrl(),
          baseURL
        });

        const validatorTemplate = 'shared.validator'.split('.').reduce((obj, part) => obj?.[part], translations);
        this.translatedValidator = parser.interpolate(validatorTemplate, {});

        // MINI FOOTER Page Translation
        const miniFooterPageTemplate = 'shared.footer_page_of'.split('.').reduce((obj, part) => obj?.[part], translations);
        this.translatedMiniFooterPage = Array.from({ length: this.pagesCount || 1 }, (_, i) => parser.interpolate(miniFooterPageTemplate, { current: i + 1, total: this.pagesCount || 1 }));

        this.scheduleFooterPositioning();
      });
    }
  }

  ngAfterViewInit(): void {
    this.resizeObserver = new ResizeObserver(() => this.scheduleFooterPositioning());
    this.observePdfContainer();
    this.footerHolders.changes.subscribe(() => this.scheduleFooterPositioning());
    this.scheduleFooterPositioning();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    clearTimeout(this.repositionTimeout);
  }

  getTranslationsForLang(lang: string): Record<string, any> | undefined {
    return this.translateService.translations[lang];
  }

  positionFooter(footerElements: Array<ElementRef>, position: FooterEnum) {
    let page = 1;
    footerElements.forEach((footer: { nativeElement: any }) => {
      if (this.pdfContainer.children.length >= page) {
        const canvas = this.pdfContainer.children[page - 1] as HTMLElement;
        const scaleLow = canvas.offsetWidth / this.maxPageWidth;
        const scaleHigh = this.maxPageWidth / canvas.offsetWidth;
        const style = footer.nativeElement.style;
        const isMiniFooter = this.documentFooterType === this.FooterTypeEnum.Mini;

        // BOTTOM
        if (position === this.FooterEnum.Bottom) {
          style.transform = 'rotate(0) ' + `scale(${scaleLow})`;
          style.transformOrigin = 'bottom left';
          style.height = isMiniFooter ? this.maxMiniFooterHeight + 'px' : this.maxFooterHeight + 'px';
          style.width = canvas.offsetWidth * scaleHigh - this.maxFooterMargin * scaleHigh * 2 + 'px';
          style.left = this.maxFooterMargin + 'px';
          style.right = null;
          style.top = canvas.offsetTop - footer.nativeElement.offsetHeight + canvas.offsetHeight - this.maxMiniFooterSpacing + 'px';
        }
        // RIGHT
        else if (position === this.FooterEnum.Right) {
          style.transform = 'rotate(-90deg) ' + `scale(${scaleLow})`;
          style.transformOrigin = 'bottom right';
          style.height = isMiniFooter ? this.maxMiniFooterHeight + 'px' : this.maxFooterHeight + 'px';
          style.width = canvas.offsetHeight * scaleHigh - this.maxFooterMargin * scaleHigh * 2 + 'px';
          style.left = null;
          style.right = isMiniFooter ? this.maxMiniFooterSpacing + 'px' : this.maxFooterMargin + 'px';
          style.top = canvas.offsetTop - footer.nativeElement.offsetHeight + this.maxFooterMargin + 'px';
        }
        // LEFT
        else if (position === this.FooterEnum.Left) {
          style.transform = 'rotate(90deg) ' + `scale(${scaleLow})`;
          style.transformOrigin = 'bottom left';
          style.height = isMiniFooter ? this.maxMiniFooterHeight + 'px' : this.maxFooterHeight + 'px';
          style.width = canvas.offsetHeight * scaleHigh - this.maxFooterMargin * scaleHigh * 2 + 'px';
          style.left = isMiniFooter ? this.maxMiniFooterSpacing + 'px' : this.maxFooterMargin + 'px';
          style.right = null;
          style.top = canvas.offsetTop - footer.nativeElement.offsetHeight + this.maxFooterMargin + 'px';
        }
        page++;
      }
    });
  }

  qrCodeUrl() {
    return `https://${this.whitelabelService?.whitelabel?.domain ? this.whitelabelService.whitelabel.domain + '/verificar' : 'valida.ae'}/${this.document?.id || ''}`;
  }

  private observePdfContainer(): void {
    this.resizeObserver?.disconnect();

    if (this.pdfContainer) {
      this.resizeObserver?.observe(this.pdfContainer);
    }
  }

  private scheduleFooterPositioning(): void {
    clearTimeout(this.repositionTimeout);
    this.repositionTimeout = setTimeout(() => {
      if (this.documentFooter && this.pdfContainer) {
        this.positionFooter(this.footerHolders?.toArray() || [], this.documentFooter);
      }
    });
  }
}
